import { useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/apiClient";
import { ChevronDown, ChevronRight, Trophy, UserMinus } from "lucide-react";
import { money } from "../lib/finance";
import { isLost, LOST_NOTE, LOST_REASONS } from "../lib/renewal";
import { Empty } from "./common";
import { CompanyLogo, CompanySelect } from "../lib/companies";

const MONTHS_FULL = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const titleKey = (s) => String(s || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("tr-TR");

function Breakdown({ title, lost, keyFn, testPrefix, color, logos }) {
  const rows = useMemo(() => {
    const map = {};
    lost.forEach((p) => {
      const label = keyFn(p) || "Belirtilmedi";
      const k = titleKey(label);
      map[k] ||= { label, count: 0, premium: 0 };
      map[k].count += 1;
      map[k].premium += Number(p.premium || 0);
    });
    return Object.values(map).sort((a, b) => (a.label === "Belirtilmedi") - (b.label === "Belirtilmedi") || b.count - a.count || b.premium - a.premium);
  }, [lost, keyFn]);
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="lost-breakdown" data-testid={testPrefix}>
      <strong className="lost-reasons-title">{title}</strong>
      {rows.map((r, i) => (
        <div key={r.label} className={`lost-reason-row ${r.label === "Belirtilmedi" ? "muted" : ""}`} data-testid={`${testPrefix}-${r.label}`}>
          <span className="lr-name">{i === 0 && r.label !== "Belirtilmedi" && rows.length > 1 && <Trophy size={12} className="lr-top" />}{logos && r.label !== "Belirtilmedi" && <CompanyLogo name={r.label} size={18} testId={`${testPrefix}-logo-${r.label}`} />}{r.label}</span>
          <div className="lr-bar"><div style={{ width: `${(r.count / max) * 100}%`, background: color }} /></div>
          <span className="lr-count">{r.count} · %{Math.round((r.count / lost.length) * 100)}</span>
          <em>{money(r.premium)}</em>
        </div>
      ))}
    </div>
  );
}

const reasonOf = (p) => p.lostReason;
const competitorOf = (p) => p.lostToCompany;

function CompanyInput({ policy, onPolicyUpdated }) {
  const [busy, setBusy] = useState(false);
  const commit = async (e) => {
    const v = e.target.value;
    if (v === (policy.lostToCompany || "")) return;
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/policies/${policy.id}/renewal-note`, { note: policy.renewalNote || LOST_NOTE, lostToCompany: v });
      onPolicyUpdated?.(data);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`lost-company-pick${busy ? " busy" : ""}`}>
      <CompanySelect label="" name="lostToCompany" value={policy.lostToCompany || ""} onChange={commit} testId={`lost-company-input-${policy.id}`} />
    </div>
  );
}

function ReasonSelect({ policy, onPolicyUpdated }) {
  const [busy, setBusy] = useState(false);
  const change = async (e) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/policies/${policy.id}/renewal-note`, { note: policy.renewalNote || LOST_NOTE, lostReason: e.target.value });
      onPolicyUpdated?.(data);
    } finally {
      setBusy(false);
    }
  };
  return (
    <select className="lost-reason-select" value={policy.lostReason || ""} disabled={busy} onChange={change} onClick={(e) => e.stopPropagation()} data-testid={`lost-reason-select-${policy.id}`}>
      <option value="">Neden seçin</option>
      {LOST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
    </select>
  );
}

export function LostCustomersPanel({ policies, onPolicyUpdated }) {
  const [open, setOpen] = useState(null);
  const lost = useMemo(() => policies.filter((p) => !p.deletedAt && isLost(p)), [policies]);
  const groups = useMemo(() => {
    const map = {};
    lost.forEach((p) => {
      const key = String(p.end || p.renewalNoteAt || "").slice(0, 7) || "—";
      (map[key] ||= []).push(p);
    });
    return Object.entries(map)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, items]) => ({ key, items, premium: items.reduce((s, p) => s + Number(p.premium || 0), 0) }));
  }, [lost]);
  const totalCount = groups.reduce((s, g) => s + g.items.length, 0);
  const totalPremium = groups.reduce((s, g) => s + g.premium, 0);
  const label = (key) => {
    const [y, m] = key.split("-");
    return m ? `${MONTHS_FULL[Number(m) - 1]} ${y}` : "Tarihsiz";
  };

  return (
    <section className="panel lost-panel" data-testid="lost-customers-panel">
      <div className="lost-head">
        <div className="settings-card-head" style={{ marginBottom: 0 }}><UserMinus size={16} /> <h2>Kaybedilen müşteriler</h2></div>
        <div className="lost-kpis">
          <span data-testid="lost-total-count"><strong>{totalCount}</strong> poliçe</span>
          <span data-testid="lost-total-premium"><strong>{money(totalPremium)}</strong> kaybedilen prim</span>
        </div>
      </div>
      <p className="privacy-note">Yenileme notu "Başka şirkete geçti" olan poliçeler, vade ayına göre gruplanır. Ayı açıp her poliçe için nedeni ve geçtiği şirketi girebilirsiniz.</p>
      {groups.length === 0 ? <Empty text="Kaybedilen müşteri kaydı yok" /> : (
        <div className="lost-layout">
        <div className="lost-table">
          {groups.map((g) => (
            <div key={g.key} className="lost-month" data-testid={`lost-month-${g.key}`}>
              <button className="lost-row" onClick={() => setOpen(open === g.key ? null : g.key)} data-testid={`lost-month-toggle-${g.key}`}>
                {open === g.key ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                <strong>{label(g.key)}</strong>
                <span>{g.items.length} poliçe</span>
                <em>{money(g.premium)}</em>
              </button>
              {open === g.key && (
                <ul className="lost-items">
                  {g.items.map((p) => (
                    <li key={p.id} data-testid={`lost-item-${p.id}`}>
                      <div><strong>{p.customer}</strong><span className="lost-item-meta">{p.company && <CompanyLogo name={p.company} size={16} />}{p.branch}{p.plate ? ` · ${p.plate}` : ""}{p.company ? ` · ${p.company}` : ""} · Vade {p.end ? new Date(`${p.end}T00:00:00`).toLocaleDateString("tr-TR") : "—"}</span></div>
                      <div className="lost-item-fields">
                        <ReasonSelect policy={p} onPolicyUpdated={onPolicyUpdated} />
                        <CompanyInput policy={p} onPolicyUpdated={onPolicyUpdated} />
                      </div>
                      <em>{money(p.premium)}</em>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        <div className="lost-reasons">
          <Breakdown title="En çok müşteri kaptırılan şirketler" lost={lost} keyFn={competitorOf} testPrefix="lost-competitors" color="#1a5fe0" logos />
          <Breakdown title="Nedenlere göre dağılım" lost={lost} keyFn={reasonOf} testPrefix="lost-reasons-list" color="#dc2626" />
        </div>
        </div>
      )}
    </section>
  );
}
